/**
 * NavigationButtons.jsx
 *
 * Back / Next navigation pair for wizard step bodies. Reads the wizard
 * context to determine which path-specific handler to invoke for each
 * direction (Explore vs Replace), and disables Next based on canProceed().
 *
 * Hidden on ENTRY, SUMMARY, and RESULTS — those steps have bespoke buttons.
 *
 * Ported from Top Hammer ToolFinder v4 (src/components/ui/NavigationButtons.jsx),
 * adapted for the MRT WizardContext API.
 *
 * @module components/wizard/NavigationButtons
 * @since 2026-05-15 (Group D Sub-Plan 1, Task D6)
 */

import { useWizard } from '@contexts/WizardContext';
import { STEPS, ENTRY_PATHS } from './wizardConstants';

export default function NavigationButtons() {
  const wizard = useWizard();
  const { currentStep, selections, canProceed } = wizard;

  // Don't show on ENTRY, SUMMARY or RESULTS — those have bespoke buttons.
  if ([STEPS.ENTRY, STEPS.SUMMARY, STEPS.RESULTS].includes(currentStep)) return null;

  const handleNext = () => {
    if (selections.entryPath === ENTRY_PATHS.EXPLORE) {
      if (currentStep === STEPS.HOLE_DIAMETER) wizard.handleHoleDiameterNext();
      else if (currentStep === STEPS.ROCK_CONDITIONS) wizard.handleRockConditionsNext();
      else wizard.handleNextExplore();
    } else if (selections.entryPath === ENTRY_PATHS.REPLACE) {
      if (currentStep === STEPS.HOLE_DIAMETER) wizard.handleHoleDiameterNext();
      else if (currentStep === STEPS.ROCK_CONDITIONS) wizard.handleRockConditionsNext();
    }
  };

  const handleBack = () => {
    if (selections.entryPath === ENTRY_PATHS.EXPLORE) {
      if (currentStep === STEPS.HOLE_DIAMETER) wizard.handleHoleDiameterPrevious();
      else if (currentStep === STEPS.ROCK_CONDITIONS) wizard.handleRockConditionsPrevious();
      else wizard.handlePreviousExplore();
    } else if (selections.entryPath === ENTRY_PATHS.REPLACE) {
      // From EQUIPMENT_SELECT, back returns to ENTRY.
      if (currentStep === STEPS.EQUIPMENT_SELECT) wizard.setCurrentStep(STEPS.ENTRY);
      else if (currentStep === STEPS.HOLE_DIAMETER) wizard.handleHoleDiameterPrevious();
      else if (currentStep === STEPS.ROCK_CONDITIONS) wizard.handleRockConditionsPrevious();
    }
  };

  return (
    <div className="flex justify-between items-center py-4 px-4 border-t border-n2">
      <button
        type="button"
        onClick={handleBack}
        className="px-4 py-2 text-n7 hover:text-n9 hover:bg-n1 rounded transition-colors"
      >
        &larr; Back
      </button>
      <button
        type="button"
        onClick={handleNext}
        disabled={!canProceed()}
        className="px-6 py-2 bg-electric-blue text-white font-normal rounded hover:bg-electric-blue/90 disabled:bg-n3 disabled:cursor-not-allowed transition-colors"
      >
        Next &rarr;
      </button>
    </div>
  );
}
